import PendingWearRecovery from './components/collection/PendingWearRecovery'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, createContext, useContext } from 'react'
import { supabase } from './lib/supabase'
import { WishListProvider } from './hooks/useWishList'
import { ProductPreferencesProvider } from './hooks/useProductPreferences'
import Layout from './components/layout/Layout'
import { DropletLogo, Wordmark } from './components/ui/BrandLogo'
import { lazy, Suspense } from 'react'
import { recordSignupAttribution } from './lib/shareCard'
import { FeatureFlagProvider } from './hooks/useFeatureFlag'
import { useCollection } from './hooks/useCollection'
import PwaUpdateControl from './components/pwa/PwaUpdateControl'
import AppRecoveryBoundary from './components/layout/AppRecoveryBoundary'
import { initGoogleAuth } from './lib/nativeAuth'
import { isNative } from './lib/platform'
import { resetAppShell, STALL_NOTICE_MS } from './lib/appShellReset'
import { isRecoveryEvent, isRecoveryHash } from './lib/passwordRecovery'
import { PENDING_AUTH_RETURN_KEY, safeAuthReturnTarget } from './lib/authReturn'

// HomePage is the landing page — keep it eager
import HomePage from './pages/HomePage'

// All other pages lazy-loaded — split into separate chunks
const CollectionPage      = lazy(() => import('./pages/CollectionPage'))
const LayeringAdvisorPage = lazy(() => import('./pages/LayeringAdvisorPage'))
const RotationPageWrapper = lazy(() => import('./pages/RotationPageWrapper'))
const RediscoverPage      = lazy(() => import('./pages/RediscoverPage'))
const FragranceDetailPage = lazy(() => import('./pages/FragranceDetailPage'))
const FragranceLogPage = lazy(() => import('./pages/FragranceLogPage'))
const ExplorePage = lazy(() => import('./pages/ExplorePage'))
const StatsPage           = lazy(() => import('./pages/StatsPage'))
const ComplimentPage = lazy(() => import('./pages/ComplimentPage'))
const LogPage             = lazy(() => import('./pages/LogPage'))
const ImportPage          = lazy(() => import('./pages/ImportPage'))
const ProfilePage         = lazy(() => import('./pages/ProfilePage'))
const AccountDeletionPage = lazy(() => import('./pages/AccountDeletionPage'))
const LoginPage           = lazy(() => import('./pages/LoginPage'))
const HealthPage          = lazy(() => import('./pages/HealthPage'))
const ImageRemovalPage = lazy(() => import('./pages/ImageRemovalPage'))
const SharedRecommendationPage = lazy(() => import('./pages/SharedRecommendationPage'))
const SharedFragranceCardPage = lazy(() => import('./pages/SharedFragranceCardPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const SupportPage = lazy(() => import('./pages/SupportPage'))
const StacksPage          = lazy(() => import('./pages/StacksPage'))
const ResetPasswordPage   = lazy(() => import('./pages/ResetPasswordPage'))

export const AuthContext = createContext(null)
export const useAuth = () => useContext(AuthContext)
const PENDING_REFERRAL_KEY = 'fos_pending_signup_referral'

export default function App() {
  const [user, setUser] = useState(undefined) // undefined = loading
  const [recoveryPending, setRecoveryPending] = useState(false)

  // Initialize Google Auth for native platforms
  useEffect(() => {
    if (isNative()) {
      initGoogleAuth().catch(err => {
        console.error('Failed to initialize Google Auth:', err)
      })
    }
  }, [])

  // Check for recovery session on first load
  useEffect(() => {
    if (isRecoveryHash(window.location.hash)) {
      setRecoveryPending(true)
    }
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null)
      // Detect PASSWORD_RECOVERY event
      if (isRecoveryEvent(event)) {
        setRecoveryPending(true)
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!user) return
    const pendingReferral = window.sessionStorage.getItem(PENDING_REFERRAL_KEY)
    if (!pendingReferral) return
    recordSignupAttribution(pendingReferral, user.id)
      .then(() => window.sessionStorage.removeItem(PENDING_REFERRAL_KEY))
      .catch(() => null)
  }, [user?.id])

  if (user === undefined) return <SplashScreen />

  return (
    <AuthContext.Provider value={{ user, recoveryPending, setRecoveryPending }}>
      <FeatureFlagProvider userId={user?.id}>
      <WishListProvider key={user?.id || 'guest'} userId={user?.id}>
      <ProductPreferencesProvider key={user?.id || 'guest'} userId={user?.id}>
      <BrowserRouter>
        <AuthReturnRedirect user={user} />
        <PwaUpdateControl />
        {user&&<PendingWearRecovery key={user.id} />}
        <AppRecoveryBoundary>
        <Suspense fallback={<SplashScreen />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login"  element={user ? <CollectionLanding fromLogin /> : <LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/health" element={<HealthPage />} />
          <Route path="/image-removal" element={<ImageRemovalPage />} />
          <Route path="/shared/:token" element={<SharedRecommendationPage />} />
          <Route path="/share/fragrance/:token" element={<SharedFragranceCardPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/support" element={<SupportPage />} />

          <Route element={<Layout />}>
            <Route path="/explore" element={<ExplorePage />} />
          </Route>
          {/* Protected routes */}
          <Route element={<ProtectedLayout />}>
            <Route index                      element={<CollectionLanding />} />
            <Route path="/collection"         element={<CollectionPage />} />
            <Route path="/layering"           element={<LayeringAdvisorPage />} />
            <Route path="/stacks"             element={<StacksPage />} />
            <Route path="/rotation"           element={<RotationPageWrapper />} />
            <Route path="/rediscover"         element={<RediscoverPage />} />
            <Route path="/fragrance/:id"      element={<FragranceDetailPage />} />
            <Route path="/fragrance/:id/log"  element={<FragranceLogPage />} />
            <Route path="/stats"              element={<StatsPage />} />
            <Route path="/log/:wearLogId/compliments" element={<ComplimentPage />} />
            <Route path="/log"               element={<LogPage />} />
            <Route path="/import"             element={<ImportPage />} />
            <Route path="/profile"            element={<ProfilePage />} />
            <Route path="/account-deletion"   element={<AccountDeletionPage />} />
          </Route>
        </Routes>
        </Suspense>
        </AppRecoveryBoundary>
      </BrowserRouter>
      </ProductPreferencesProvider>
      </WishListProvider>
      </FeatureFlagProvider>
    </AuthContext.Provider>
  )
}

function AuthReturnRedirect({ user }) {
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    if (!user) return
    let stored = ''
    try { stored = window.sessionStorage.getItem(PENDING_AUTH_RETURN_KEY) || '' } catch { return }
    const target = safeAuthReturnTarget(stored)
    try { window.sessionStorage.removeItem(PENDING_AUTH_RETURN_KEY) } catch { /* The validated route can still open. */ }
    if (target && `${location.pathname}${location.search}` !== target) navigate(target, { replace: true })
  }, [user?.id, location.pathname, location.search, navigate])
  return null
}

// A splash that never ends looks like a frozen app (FOS-HOME-LAYER-FREEZE-001:
// a page's code file stalled and the phone sat on this screen). After a
// generous wait it offers a one-tap reset instead of waiting forever.
function SplashScreen() {
  const [stalled, setStalled] = useState(false)
  useEffect(() => {
    const timer = window.setTimeout(() => setStalled(true), STALL_NOTICE_MS)
    return () => window.clearTimeout(timer)
  }, [])
  return (
    <div className="min-h-screen bg-charcoal-900 flex flex-col items-center justify-center gap-4" data-splash-screen>
      <DropletLogo className="w-16 h-16" />
      <p className="text-xl tracking-widest"><Wordmark className="text-xl tracking-widest" /></p>
      <div className="flex gap-1 mt-2">
        {[0,1,2].map(i => (
          <div key={i} className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce"
               style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>
      {stalled && (
        <div role="alert" className="mt-4 flex flex-col items-center gap-3 px-6 text-center" data-splash-stalled>
          <p className="text-sm text-cream/70">This is taking longer than it should.</p>
          <button type="button" onClick={() => resetAppShell()} className="flex min-h-11 items-center justify-center rounded-xl bg-gold px-6 py-3 font-semibold text-charcoal-900">
            Reload
          </button>
        </div>
      )}
    </div>
  )
}

export function CollectionLanding({ fromLogin = false }) {
  const { allFragrances, loading, error } = useCollection()
  if (loading) return <SplashScreen />
  return !error && allFragrances.length === 0 ? <Navigate to="/explore" replace /> : fromLogin ? <Navigate to="/" replace /> : <HomePage />
}

function ProtectedLayout() {
  const { user, recoveryPending } = useAuth()
  if (recoveryPending) return <Navigate to="/reset-password" replace />
  if (!user) return <Navigate to="/explore" replace />
  return <Layout />
}
